/**
 * useCouncilQueue — polls the read-only Council Review Queue endpoint
 * GET /api/v1/council/queue.
 *
 * Returns the authoritative items[] feed produced by the backend
 * council_queue route (which itself reads on-disk Council decision
 * artifacts and merges the enriched per-strategy status). Never
 * fabricates Council decisions. If the endpoint is unreachable or
 * returns an error envelope, the hook surfaces a clean
 * `status: 'UNAVAILABLE'` / `error` state -- it does NOT clobber the
 * last good render with a transient blip.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';

export type QueueTopStatus = 'LIVE' | 'LIMITED' | 'UNAVAILABLE';

export type ReviewStatus =
  | 'approved'
  | 'needs_more_data'
  | 'rejected'
  | 'pending'
  | 'unavailable';

export interface EvidenceWindow {
  days_with_evidence: number;
  target_days: number;
  event_count: number;
}

export interface CouncilQueueItem {
  strategy_id: string;
  strategy_name: string;
  account_id: string | null;
  account_label: string | null;
  review_status: ReviewStatus | string;
  council_decision: string;
  confidence: number | null;
  provider_votes: Record<string, string | null>;
  final_aggregation: string | null;
  last_reviewed_at: string | null;
  next_review_due_at: string | null;
  evidence_window: EvidenceWindow;
  next_required_evidence: string[];
  activation_status: string;
  data_collection_mode: boolean;
  live_trading_blocked: boolean;
  source_files: string[];
  source_status: 'LIVE' | 'LIMITED' | 'UNAVAILABLE' | string;
}

export interface CouncilQueueDiagnostics {
  files_scanned: string[];
  missing_sources: string[];
  errors: string[];
}

export interface CouncilQueuePayload {
  status: QueueTopStatus;
  generated_at: string;
  items: CouncilQueueItem[];
  diagnostics_raw: CouncilQueueDiagnostics;
}

export interface UseCouncilQueueResult {
  payload: CouncilQueuePayload | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 60_000;

export function useCouncilQueue(): UseCouncilQueueResult {
  const [payload, setPayload] = useState<CouncilQueuePayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const firstLoadRef = useRef<boolean>(true);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${apiV1Base()}/council/queue`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const raw = await resp.text();
      let body: CouncilQueuePayload;
      try {
        body = JSON.parse(raw) as CouncilQueuePayload;
      } catch {
        throw new Error('Council queue returned non-JSON');
      }
      // Validate minimum shape; degrade to UNAVAILABLE if missing.
      if (!body || typeof body !== 'object' || !Array.isArray(body.items)) {
        setPayload({
          status: 'UNAVAILABLE',
          generated_at: new Date().toISOString(),
          items: [],
          diagnostics_raw: {
            files_scanned: [],
            missing_sources: [],
            errors: ['Council queue payload missing required fields'],
          },
        });
      } else {
        setPayload(body);
      }
      setError(null);
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Council queue request failed';
      if (firstLoadRef.current) {
        setError(msg);
        setLoading(false);
        firstLoadRef.current = false;
      }
      // After first load, swallow transient errors to keep last-good render.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (!cancelled) await load();
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [load]);

  return { payload, loading, error, refetch: load };
}
